import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X, Building2, Briefcase, AlertCircle, Check, ChevronRight, ChevronLeft,
  Package, UserPlus, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import useBackdropDismiss from '@/hooks/useBackdropDismiss';
import {
  provisionTenant,
  listGrantableApps,
  listAiEmployeeDefinitions,
} from './api';
import { isValidSlug, deriveSlug } from './helpers';

/**
 * Phase 2 — one-step customer setup.
 *
 * ## What this replaces
 *
 * Creating a tenant and provisioning its role copies was already a single
 * action. Everything after it was not: grant each app on the Apps tab,
 * invite the first administrator on the Users tab, then satisfy yourself
 * by hand that the result is usable. Three screens and a judgement call,
 * repeated per customer.
 *
 * ## Why this is a separate component from TenantFormModal
 *
 * `TenantFormModal` serves create AND edit from one form, and edit is the
 * more delicate of the two — it exposes status transitions and treats type
 * as read-only for good reasons. Threading three extra wizard steps
 * through a component whose other mode must not change them is how you
 * break edit while building create. This modal owns provisioning; edit
 * stays where it is.
 *
 * ## The result step is the point
 *
 * `POST /tenants/provision` returns 200 even when a step failed — there is
 * no cross-collection transaction, so a failed grant leaves a real tenant
 * behind rather than deleting it, and the endpoint reports rather than
 * rolls back. That contract is only worth having if the UI shows it. A
 * screen that renders "Created!" on any 200 would throw away the
 * distinction the backend went to trouble to preserve, and the operator
 * would find out days later that a customer has no apps.
 *
 * So the final step renders per-step outcomes and the verification
 * checks, and says plainly which of the remaining screens to finish the
 * job on. Success and partial success look different.
 *
 * Props
 *   isOpen      boolean
 *   allTenants  array, for the parent picker
 *   onClose     () => void
 *   onSuccess   (tenant) => void — fired once, on close after a provision
 */

// AI Employees sits before Modules deliberately. Aidan's framing on 17 Sep
// was that a customer "likely bought BOSS for an employee" — the employee is
// the thing they came for, the modules are what it runs on. Asking in that
// order matches how the sale happened.
//
// The two are asked SEPARATELY and neither is derived from the other:
// nothing in the data model links a definition to an app, so inferring one
// would mean inventing a mapping nobody has agreed.
const STEPS = ['Customer', 'AI Employees', 'Modules', 'Administrator', 'Result'];

// Named rather than numbered. Inserting AI Employees shifted every index
// after it, and the step logic was five scattered integer comparisons —
// exactly the shape where one gets missed and the wizard shows the wrong
// pane or the wrong button. Deriving them from STEPS means the next
// insertion cannot renumber anything by hand.
const S_CUSTOMER = STEPS.indexOf('Customer');
const S_AI = STEPS.indexOf('AI Employees');
const S_MODULES = STEPS.indexOf('Modules');
const S_ADMIN = STEPS.indexOf('Administrator');
const S_RESULT = STEPS.indexOf('Result');

export default function ProvisionTenantModal({
  isOpen,
  allTenants = [],
  onClose,
  onSuccess,
}) {
  const [step, setStep] = useState(0);

  // Step 1 — tenant
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [tenantType, setTenantType] = useState('account');
  const [parentTenantId, setParentTenantId] = useState('');

  // Step 2 — apps
  const [apps, setApps] = useState(null); // null = not yet loaded
  const [appsError, setAppsError] = useState('');
  const [selectedApps, setSelectedApps] = useState([]);

  const [definitions, setDefinitions] = useState(null); // null = not loaded
  const [definitionsError, setDefinitionsError] = useState('');
  const [selectedDefinitions, setSelectedDefinitions] = useState([]);

  // Step 3 — first administrator
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');

  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Second arg is `isBusy` — the hook refuses to dismiss mid-flight, so a
  // stray click on the backdrop cannot abandon a provision that is already
  // creating a tenant server-side.
  const backdrop = useBackdropDismiss(onClose, submitting);

  const reset = useCallback(() => {
    setStep(S_CUSTOMER);
    setName(''); setSlug(''); setSlugTouched(false);
    setTenantType('account'); setParentTenantId('');
    setSelectedApps([]); setSelectedDefinitions([]);
    setAdminEmail(''); setAdminName('');
    setErrors({}); setBanner(''); setResult(null); setSubmitting(false);
  }, []);

  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen, reset]);

  // Slug auto-derives from name until the operator edits it — same
  // behaviour as TenantFormModal, so the two feel like one product.
  useEffect(() => {
    if (!slugTouched) setSlug(deriveSlug(name));
  }, [name, slugTouched]);

  useEffect(() => {
    if (!isOpen || apps !== null) return;
    (async () => {
      try {
        const res = await listGrantableApps();
        const data = res.ok ? await res.json() : null;
        setApps(Array.isArray(data) ? data : (data?.apps ?? []));
      } catch {
        // Not fatal. A tenant with no apps is valid, so a catalogue that
        // will not load should not block creating the customer.
        setApps([]);
        setAppsError('Could not load the module catalogue. You can grant modules later from the Apps tab.');
      }
    })();
  }, [isOpen, apps]);

  useEffect(() => {
    if (!isOpen || definitions !== null) return;
    (async () => {
      try {
        const res = await listAiEmployeeDefinitions();
        const data = res.ok ? await res.json() : null;
        setDefinitions(
          Array.isArray(data) ? data : (data?.definitions ?? []),
        );
      } catch {
        // Same tolerance as the module catalogue: a tenant with no AI
        // employees is valid, so a catalogue that will not load must not
        // block creating the customer.
        setDefinitions([]);
        setDefinitionsError(
          'Could not load the AI employee catalogue. You can assign employees later from the AI Employees tab.',
        );
      }
    })();
  }, [isOpen, definitions]);

  const parentOptions = useMemo(
    () => allTenants.filter((t) => t.tenant_type === 'root' || t.tenant_type === 'partner'),
    [allTenants],
  );

  const validateTenantStep = () => {
    const errs = {};
    if (!name.trim()) errs.name = 'Name is required.';
    if (!slug.trim()) errs.slug = 'Slug is required.';
    else if (!isValidSlug(slug)) errs.slug = 'Lowercase, alphanumeric and hyphens, 1–64 characters.';
    if (tenantType === 'account' && !parentTenantId) {
      errs.parentTenantId = 'An account tenant needs a parent.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateAdminStep = () => {
    const errs = {};
    // Both optional — a customer is sometimes created before anyone knows
    // who will run it. But half an administrator is a mistake, not a
    // choice, so an email without a name (or vice versa) is caught here
    // rather than becoming a confusing invite.
    if (adminEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail.trim())) {
      errs.adminEmail = 'Enter a valid email address.';
    }
    if (adminName.trim() && !adminEmail.trim()) {
      errs.adminEmail = 'Add an email address, or clear the name.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => {
    if (step === S_CUSTOMER && !validateTenantStep()) return;
    if (step === S_ADMIN && !validateAdminStep()) return;
    setErrors({});
    setStep((s) => s + 1);
  };

  const back = () => {
    setErrors({});
    setBanner('');
    setStep((s) => Math.max(S_CUSTOMER, s - 1));
  };

  const submit = async () => {
    if (submitting) return;
    // The admin step's button is Create, not Next, so `next()` never runs
    // for it and its validation has to be re-asserted here. Caught by
    // `test_rejects_a_name_without_an_email`: a wizard that validates on
    // navigation silently skips the last step, which is the one that
    // submits.
    if (!validateAdminStep()) return;
    setSubmitting(true);
    setBanner('');
    try {
      const body = {
        tenant: {
          name: name.trim(),
          slug: slug.trim(),
          tenant_type: tenantType,
          ...(parentTenantId ? { parent_tenant_id: parentTenantId } : {}),
        },
        app_ids: selectedApps,
        definition_ids: selectedDefinitions,
        ...(adminEmail.trim()
          ? {
              admin_email: adminEmail.trim(),
              admin_name: adminName.trim() || adminEmail.trim().split('@')[0],
            }
          : {}),
      };
      const res = await provisionTenant(body);
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        // Only tenant creation itself fails this way. A duplicate slug is
        // the common case, and it belongs on the field the operator typed
        // it into, not in a banner three steps away.
        const detail = typeof data?.detail === 'string' ? data.detail : '';
        if (res.status === 409 && detail.toLowerCase().includes('slug')) {
          setErrors({ slug: detail });
          setStep(S_CUSTOMER);
        } else {
          setBanner(detail || 'Could not create the customer.');
        }
        return;
      }

      setResult(data);
      setStep(S_RESULT);
      // Deliberately not a success toast. Whether this went well is the
      // question the result step exists to answer, and a green toast over
      // a partial failure is worse than no toast at all.
    } catch {
      setBanner('Could not reach the server. Nothing was created.');
    } finally {
      setSubmitting(false);
    }
  };

  const finish = () => {
    if (result?.tenant) onSuccess?.(result.tenant);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      {...backdrop}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="provision-tenant-modal"
    >
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-boss-surface border border-boss-border shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-boss-border">
          <h2 className="text-lg font-semibold text-boss-text">Set up a customer</h2>
          <button
            onClick={onClose}
            className="text-boss-text-muted hover:text-boss-text"
            data-testid="provision-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <Stepper current={step} />

        <div className="px-5 py-4">
          {banner && (
            <div className="mb-4 flex gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700/40 p-3">
              <AlertCircle size={16} className="text-red-600 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{banner}</p>
            </div>
          )}

          {step === S_CUSTOMER && (
            <TenantStep
              {...{ name, setName, slug, setSlug, setSlugTouched, tenantType,
                setTenantType, parentTenantId, setParentTenantId,
                parentOptions, errors }}
            />
          )}

          {step === S_AI && (
            <AiEmployeesStep
              definitions={definitions}
              definitionsError={definitionsError}
              selected={selectedDefinitions}
              setSelected={setSelectedDefinitions}
            />
          )}

          {step === S_MODULES && (
            <AppsStep
              apps={apps}
              appsError={appsError}
              selected={selectedApps}
              setSelected={setSelectedApps}
            />
          )}

          {step === S_ADMIN && (
            <AdminStep
              {...{ adminEmail, setAdminEmail, adminName, setAdminName, errors }}
            />
          )}

          {step === S_RESULT && result && <ResultStep result={result} />}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-boss-border">
          {step > S_CUSTOMER && step < S_RESULT ? (
            <button
              onClick={back}
              className="btn-secondary inline-flex items-center gap-1.5 text-sm"
              data-testid="provision-back"
            >
              <ChevronLeft size={15} /> Back
            </button>
          ) : <span />}

          {step < S_ADMIN && (
            <button
              onClick={next}
              className="btn-primary inline-flex items-center gap-1.5 text-sm"
              data-testid="provision-next"
            >
              Next <ChevronRight size={15} />
            </button>
          )}

          {step === S_ADMIN && (
            <button
              onClick={submit}
              disabled={submitting}
              className="btn-primary inline-flex items-center gap-1.5 text-sm disabled:opacity-50"
              data-testid="provision-submit"
            >
              {submitting ? 'Setting up…' : 'Create customer'}
            </button>
          )}

          {step === S_RESULT && (
            <button
              onClick={finish}
              className="btn-primary text-sm"
              data-testid="provision-done"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Steps ───────────────────────────────────────────────────────────

function Stepper({ current }) {
  return (
    <div className="flex items-center gap-1 px-5 py-3 border-b border-boss-border">
      {STEPS.map((label, i) => (
        <React.Fragment key={label}>
          <span
            className={
              'text-xs font-medium ' +
              (i === current
                ? 'text-violet-600 dark:text-violet-300'
                : i < current
                  ? 'text-boss-text-secondary'
                  : 'text-boss-text-muted')
            }
            data-testid={`provision-step-${i}`}
          >
            {i < current ? <Check size={12} className="inline mr-1 -mt-0.5" /> : null}
            {label}
          </span>
          {i < STEPS.length - 1 && (
            <ChevronRight size={12} className="text-boss-text-muted" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function Field({ label, error, children, hint }) {
  return (
    <div className="mb-4">
      <label className="text-xs text-boss-text-secondary block mb-1.5">{label}</label>
      {children}
      {hint && !error && (
        <p className="text-[11px] text-boss-text-muted mt-1">{hint}</p>
      )}
      {error && <p className="text-[11px] text-red-600 mt-1">{error}</p>}
    </div>
  );
}

function TenantStep({
  name, setName, slug, setSlug, setSlugTouched, tenantType, setTenantType,
  parentTenantId, setParentTenantId, parentOptions, errors,
}) {
  return (
    <div>
      <Field label="Customer name" error={errors.name}>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="input-field text-sm w-full"
          placeholder="Acme Corporation"
          data-testid="provision-name"
        />
      </Field>

      <Field
        label="Slug"
        error={errors.slug}
        hint="Used in URLs and cannot be changed easily later."
      >
        <input
          type="text"
          value={slug}
          onChange={(e) => { setSlugTouched(true); setSlug(e.target.value); }}
          className="input-field text-sm w-full font-mono"
          placeholder="acme-corporation"
          data-testid="provision-slug"
        />
      </Field>

      <Field label="Type">
        <div className="flex gap-2">
          {[
            { id: 'account', label: 'Account', icon: Building2,
              hint: 'An end customer' },
            { id: 'partner', label: 'Partner', icon: Briefcase,
              hint: 'Can create sub-tenants' },
          ].map(({ id, label, icon: Icon, hint }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTenantType(id)}
              data-testid={`provision-type-${id}`}
              className={
                'flex-1 text-left p-3 rounded-lg border transition ' +
                (tenantType === id
                  ? 'border-violet-400 bg-violet-50 dark:bg-violet-600/15'
                  : 'border-boss-border hover:border-boss-border-hover')
              }
            >
              <span className="flex items-center gap-1.5 text-sm font-medium text-boss-text">
                <Icon size={14} /> {label}
              </span>
              <span className="text-[11px] text-boss-text-muted">{hint}</span>
            </button>
          ))}
        </div>
      </Field>

      <Field label="Parent tenant" error={errors.parentTenantId}>
        <select
          value={parentTenantId}
          onChange={(e) => setParentTenantId(e.target.value)}
          className="input-field text-sm w-full"
          data-testid="provision-parent"
        >
          <option value="">Select a parent…</option>
          {parentOptions.map((t) => (
            <option key={t.tenant_id} value={t.tenant_id}>
              {t.name} ({t.tenant_type})
            </option>
          ))}
        </select>
      </Field>
    </div>
  );
}

function AiEmployeesStep({ definitions, definitionsError, selected, setSelected }) {
  const toggle = (id) =>
    setSelected((cur) =>
      cur.includes(id) ? cur.filter((d) => d !== id) : [...cur, id]);

  if (definitions === null) {
    return <p className="text-sm text-boss-text-muted">Loading AI employees…</p>;
  }

  return (
    <div>
      <p className="text-sm text-boss-text-secondary mb-3">
        Choose the AI employees this customer should have. You can change
        this later from the AI Employees tab.
      </p>

      {definitionsError && (
        <div className="mb-3 flex gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/40 p-3">
          <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700 dark:text-amber-300">{definitionsError}</p>
        </div>
      )}

      {definitions.length === 0 && !definitionsError && (
        <p className="text-sm text-boss-text-muted">
          No AI employees are available to assign.
        </p>
      )}

      <div className="space-y-2">
        {definitions.map((def) => {
          const id = def.definition_id;
          const on = selected.includes(id);
          return (
            <button
              key={id}
              type="button"
              onClick={() => toggle(id)}
              data-testid={`provision-ai-${id}`}
              className={
                'w-full flex items-start gap-2.5 p-3 rounded-lg border text-left transition ' +
                (on
                  ? 'border-violet-400 bg-violet-50 dark:bg-violet-600/15'
                  : 'border-boss-border hover:border-boss-border-hover')
              }
            >
              <span
                className={
                  'mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 ' +
                  (on ? 'bg-violet-600 border-violet-600' : 'border-boss-border')
                }
              >
                {on && <Check size={11} className="text-white" />}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-boss-text">
                  {def.name || id}
                </span>
                {def.description && (
                  <span className="block text-xs text-boss-text-muted mt-0.5">
                    {def.description}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}


function AppsStep({ apps, appsError, selected, setSelected }) {
  const toggle = (appId) =>
    setSelected((cur) =>
      cur.includes(appId) ? cur.filter((a) => a !== appId) : [...cur, appId]);

  if (apps === null) {
    return <p className="text-sm text-boss-text-muted">Loading modules…</p>;
  }

  return (
    <div>
      <p className="text-sm text-boss-text-secondary mb-3">
        Choose the modules this customer should have. You can change this
        later from the Apps tab.
      </p>

      {appsError && (
        <div className="mb-3 flex gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/40 p-3">
          <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700 dark:text-amber-300">{appsError}</p>
        </div>
      )}

      {apps.length === 0 && !appsError && (
        <p className="text-sm text-boss-text-muted">
          No modules are available to grant. The customer will be created
          with the AI Employees workspace only.
        </p>
      )}

      <div className="space-y-2">
        {apps.map((app) => {
          const id = app.app_id;
          const on = selected.includes(id);
          return (
            <button
              key={id}
              type="button"
              onClick={() => toggle(id)}
              data-testid={`provision-app-${id}`}
              className={
                'w-full flex items-start gap-2.5 p-3 rounded-lg border text-left transition ' +
                (on
                  ? 'border-violet-400 bg-violet-50 dark:bg-violet-600/15'
                  : 'border-boss-border hover:border-boss-border-hover')
              }
            >
              <span
                className={
                  'mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 ' +
                  (on ? 'bg-violet-600 border-violet-600' : 'border-boss-border')
                }
              >
                {on && <Check size={11} className="text-white" />}
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-sm font-medium text-boss-text">
                  <Package size={13} /> {app.name || id}
                </span>
                {app.description && (
                  <span className="block text-[11px] text-boss-text-muted mt-0.5">
                    {app.description}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AdminStep({ adminEmail, setAdminEmail, adminName, setAdminName, errors }) {
  return (
    <div>
      <p className="text-sm text-boss-text-secondary mb-3">
        Invite the customer's first administrator. They will be able to
        invite their own users and manage their own roles.
      </p>

      <Field label="Email" error={errors.adminEmail}>
        <input
          type="email"
          value={adminEmail}
          onChange={(e) => setAdminEmail(e.target.value)}
          className="input-field text-sm w-full"
          placeholder="admin@acme.com"
          data-testid="provision-admin-email"
        />
      </Field>

      <Field label="Name">
        <input
          type="text"
          value={adminName}
          onChange={(e) => setAdminName(e.target.value)}
          className="input-field text-sm w-full"
          placeholder="Jane Doe"
          data-testid="provision-admin-name"
        />
      </Field>

      <p className="text-[11px] text-boss-text-muted">
        Both optional — leave blank to create the customer now and invite
        an administrator later from the Users tab.
      </p>
    </div>
  );
}

// ─── Result ──────────────────────────────────────────────────────────

const STEP_LABELS = {
  create_tenant: 'Customer created',
  grant_app: 'Module granted',
  invite_admin: 'Administrator invited',
  verify: 'Checked',
};

/**
 * Where to finish a step that did not land. The endpoint reports failures
 * rather than rolling back, so every failure has a screen that can
 * complete it by hand — saying which one is the difference between a
 * report and a dead end.
 */
const REMEDY = {
  grant_app: 'Grant it from the Apps tab on this customer.',
  invite_admin: 'Invite them from the Users tab on this customer.',
};

function ResultStep({ result }) {
  const { steps = [], verification = {}, ready, tenant } = result;
  const failed = steps.filter((s) => s.status === 'failed');
  const inviteLink = steps.find((s) => s.step === 'invite_admin')?.invite_link;
  const failedChecks = (verification.checks || []).filter((c) => !c.ok);

  return (
    <div data-testid="provision-result">
      <div
        className={
          'flex gap-2.5 p-3 rounded-lg border mb-4 ' +
          (ready
            ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-700/40'
            : 'border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/40')
        }
      >
        {ready
          ? <Check size={16} className="text-emerald-600 shrink-0 mt-0.5" />
          : <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />}
        <div>
          <p className={'text-sm font-medium ' + (ready ? 'text-emerald-800 dark:text-emerald-300' : 'text-amber-800 dark:text-amber-300')}>
            {ready
              ? `${tenant?.name} is set up and ready.`
              : `${tenant?.name} was created, but some steps did not finish.`}
          </p>
          {!ready && (
            <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-0.5">
              The customer exists and nothing was undone. Finish the
              remaining steps below.
            </p>
          )}
        </div>
      </div>

      <ul className="space-y-1.5 mb-4">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-2 text-sm" data-testid={`result-step-${s.step}`}>
            <span className="mt-0.5 shrink-0">
              {s.status === 'ok' && <Check size={14} className="text-emerald-600" />}
              {s.status === 'failed' && <X size={14} className="text-red-600" />}
              {s.status === 'skipped' && <span className="text-boss-text-muted text-xs">–</span>}
              {s.status === 'incomplete' && <AlertCircle size={14} className="text-amber-600" />}
            </span>
            <span className="min-w-0">
              <span className="text-boss-text">
                {STEP_LABELS[s.step] || s.step}
                {s.app_id ? ` — ${s.app_id}` : ''}
                {s.email ? ` — ${s.email}` : ''}
              </span>
              {s.detail && (
                <span className="block text-[11px] text-boss-text-muted">{s.detail}</span>
              )}
              {s.status === 'failed' && REMEDY[s.step] && (
                <span className="block text-[11px] text-boss-text-secondary mt-0.5">
                  {REMEDY[s.step]}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>

      {failedChecks.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-medium text-boss-text-secondary mb-1.5">
            Checks that did not pass
          </p>
          <ul className="space-y-1">
            {failedChecks.map((c) => (
              <li key={c.check} className="text-[11px] text-boss-text-muted" data-testid={`result-check-${c.check}`}>
                <span className="font-mono">{c.check}</span>
                {c.detail ? ` — ${c.detail}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {inviteLink && (
        <div className="rounded-lg border border-boss-border p-3">
          <p className="text-xs text-boss-text-secondary mb-1.5 flex items-center gap-1.5">
            <UserPlus size={13} /> Invite link
          </p>
          <div className="flex items-center gap-2">
            <code className="text-[11px] text-boss-text-muted truncate flex-1">{inviteLink}</code>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard?.writeText(inviteLink);
                toast.success('Invite link copied');
              }}
              className="btn-secondary text-xs inline-flex items-center gap-1 shrink-0"
              data-testid="copy-invite-link"
            >
              <Copy size={12} /> Copy
            </button>
          </div>
          <p className="text-[11px] text-boss-text-muted mt-1.5">
            An email has also been sent. Copy this if it does not arrive.
          </p>
        </div>
      )}

      {failed.length === 0 && !ready && failedChecks.length === 0 && (
        <p className="text-[11px] text-boss-text-muted">
          Every step reported success but a verification check did not
          pass. This usually means role provisioning needs re-running.
        </p>
      )}
    </div>
  );
}
